import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button, NumberBox } from "devextreme-react";
import axios from "axios";
import { alert, confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import { ApplyMethod, YesNo } from "../../../../../enum/Enum";

const emptyGuid = "00000000-0000-0000-0000-000000000000";

const CONFIRM_ZERO_ITEM_DISCOUNT_MESSAGE =
    "No Item Discount Entered.<br/><br/>Do You Wish to Continue and Apply ZERO Item Discount to ALL Line Items?";

const isZeroOrEmptyDiscount = (value) => {
    if (value == null || value === "") {
        return true;
    }
    if (typeof value === "string" && value.trim() === "") {
        return true;
    }
    const numeric = Number(value);
    return !Number.isFinite(numeric) || numeric === 0;
};

function ResetItemDiscountPopup(props) {
    const {
        showPopup = {},
        setShowPopup,
        setLoadPanelVisible,
        onSuccess,
        profileIncludeDiscountInUnitPrice,
        applyMethod,
        oidc,
    } = props;

    const visible = showPopup.visible === true;
    const headerRowId = showPopup.headerRowId;
    const defaultDetailDiscount = showPopup.defaultDetailDiscount;
    const accessToken = oidc?.user?.access_token;

    const [discountPercent, setDiscountPercent] = useState(0);

    const canRunResetItemDiscount = useCallback(() => {
        const includeDiscountInUnitPrice = profileIncludeDiscountInUnitPrice === YesNo.Yes;
        const applyMethodSpecifyPrice = applyMethod === ApplyMethod.ApplyMethodSpecifyPrice;
        return !includeDiscountInUnitPrice && !applyMethodSpecifyPrice;
    }, [profileIncludeDiscountInUnitPrice, applyMethod]);


    const closePopup = useCallback(() => {
        setShowPopup({ visible: false });
        setDiscountPercent(0);
    }, [setShowPopup]);

    const handleApply = useCallback(async () => {
        if (!headerRowId || headerRowId === emptyGuid) {
            alert(MESSAGES.PLEASE_SELECT_TRANSACTION, MESSAGES.ERROR);
            return;
        }
        if (!canRunResetItemDiscount()) {
            return;
        }

        let discountToApply = discountPercent ?? 0;
        if (isZeroOrEmptyDiscount(discountPercent)) {
            const confirmed = await confirm(
                CONFIRM_ZERO_ITEM_DISCOUNT_MESSAGE,
                MESSAGES.CONFIRM
            );
            if (!confirmed) {
                return;
            }
            discountToApply = 0;
            setDiscountPercent(0);
        }

        if (setLoadPanelVisible) {
            setLoadPanelVisible(true);
        }
        try {
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.ResetItemDiscount}`,
                {
                    RTransactionheader: headerRowId,
                    Discountpercent: discountToApply,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            const payload =
                res.data?.value != null && typeof res.data.value === "object"
                    ? res.data.value
                    : res.data;
            if (!payload?.success) {
                alert(payload?.message || MESSAGES.ERROR, MESSAGES.ERROR);
                return;
            }
            notify(
                {
                    message: payload.message || "Item discounts reset.",
                    width: 280,
                },
                "success",
                3000
            );
            closePopup();
            if (onSuccess) {
                onSuccess(discountToApply);
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR);
        } finally {
            if (setLoadPanelVisible) {
                setLoadPanelVisible(false);
            }
        }
    }, [
        accessToken,
        headerRowId,
        discountPercent,
        canRunResetItemDiscount,
        closePopup,
        onSuccess,
        setLoadPanelVisible,
    ]);

    return (
        <Popup
            visible={visible}
            onHiding={closePopup}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showTitle={true}
            title="Reset Item Discount"
            width={360}
            height="auto"
            showCloseButton={true}
        >
            <div style={{ padding: "8px 12px 12px 12px" }}>
                <div style={{ marginBottom: 12 }}>
                    This option allows you to reset the item discount percent. Please note that existing Item
                    Discount will be overwritten in all items with the discount entered here.
                </div>
                <NumberBox
                    value={discountPercent}
                    min={0}
                    format="#0.##"
                    showSpinButtons={true}
                    onValueChanged={(e) => setDiscountPercent(e.value ?? 0)}
                />
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        paddingTop: 16,
                    }}
                >
                    <Button text="OK" type="default" onClick={handleApply} />
                    <Button text="Cancel" onClick={closePopup} />
                </div>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(ResetItemDiscountPopup);
